import { useEffect, useRef, useState } from "react";
import { ChevronUp, ChevronDown, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const inputCls =
  "w-full rounded-lg border border-line bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand";

const Field = ({ label, value, onChange, textarea, testId, placeholder }) => (
  <label className="block">
    <span className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</span>
    {textarea ? (
      <textarea data-testid={testId} rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputCls} />
    ) : (
      <input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputCls} />
    )}
  </label>
);

const ImageField = ({ label, value, onChange, testId }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatApiError(err, "Caricamento fallito"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="block">
      <span className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1">{label}</span>
      <div className="flex items-center gap-2">
        <input
          data-testid={testId}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… oppure carica un file"
          className={inputCls}
        />
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onFile} data-testid={`${testId}-file-input`} />
        <button
          type="button"
          data-testid={`${testId}-upload-button`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 rounded-lg bg-ink text-white text-xs font-display font-semibold px-3.5 py-2.5 hover:bg-graphite transition-colors disabled:opacity-40 shrink-0"
        >
          <Upload size={13} />
          {uploading ? "Caricamento…" : "Carica"}
        </button>
      </div>
      {value && (
        <img src={value} alt="" className="mt-2 h-16 rounded-md object-cover border border-line" />
      )}
    </div>
  );
};

const LAYOUT_LABELS = {
  cover: "Cover",
  split: "Split",
  cards: "Cards",
  pillars: "Pilastri",
  services: "Servizi",
  steps: "Step",
  numbers: "Numeri",
  sectors: "Settori",
  metrics: "Metriche",
  contact: "Contatti",
};

export default function SlideEditor() {
  const [slides, setSlides] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/slides")
      .then((r) => setSlides(r.data.slides))
      .catch(() => toast.error("Errore nel caricamento delle slide"));
  }, []);

  const patch = (i, p) => setSlides((s) => s.map((sl, idx) => (idx === i ? { ...sl, ...p } : sl)));
  const patchItem = (i, j, p) =>
    setSlides((s) => s.map((sl, idx) => (idx === i ? { ...sl, items: sl.items.map((it, k) => (k === j ? { ...it, ...p } : it)) } : sl)));
  const addItem = (i) =>
    setSlides((s) => s.map((sl, idx) => (idx === i ? { ...sl, items: [...(sl.items || []), { title: "", desc: "" }] } : sl)));
  const removeItem = (i, j) =>
    setSlides((s) => s.map((sl, idx) => (idx === i ? { ...sl, items: sl.items.filter((_, k) => k !== j) } : sl)));
  const patchMetric = (i, j, p) =>
    setSlides((s) => s.map((sl, idx) => (idx === i ? { ...sl, metrics: sl.metrics.map((m, k) => (k === j ? { ...m, ...p } : m)) } : sl)));
  const addMetric = (i) =>
    setSlides((s) => s.map((sl, idx) => (idx === i ? { ...sl, metrics: [...(sl.metrics || []), { value: "", label: "" }] } : sl)));
  const removeMetric = (i, j) =>
    setSlides((s) => s.map((sl, idx) => (idx === i ? { ...sl, metrics: sl.metrics.filter((_, k) => k !== j) } : sl)));
  const move = (i, d) =>
    setSlides((s) => {
      const arr = [...s];
      const j = i + d;
      if (j < 0 || j >= arr.length) return s;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/slides", { slides });
      toast.success("Slide aggiornate");
    } catch (e) {
      toast.error(formatApiError(e, "Errore nel salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  if (!slides) return <div className="font-mono text-xs text-slate-400 uppercase tracking-widest">Caricamento…</div>;

  return (
    <div data-testid="slide-editor">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display font-extrabold text-xl tracking-tight text-ink">Contenuti slide</h2>
          <p className="text-sm text-slate-500 mt-0.5">Modifica testi, immagini e ordine delle slide.</p>
        </div>
        <button
          data-testid="save-slides-button"
          onClick={save}
          disabled={saving}
          className="rounded-full bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm px-6 py-2.5 transition-colors disabled:opacity-40"
        >
          {saving ? "Salvataggio…" : "Salva modifiche"}
        </button>
      </div>

      <div className="space-y-3">
        {slides.map((sl, i) => (
          <div key={sl.id} className="bg-white border border-line rounded-xl overflow-hidden" data-testid={`slide-row-${sl.id}`}>
            <div className="flex items-center gap-3 px-4 py-3">
              <span className="font-mono text-xs text-slate-400 w-7">{String(i + 1).padStart(2, "0")}</span>
              <div className="flex-1 min-w-0">
                <span className="font-display font-semibold text-sm text-ink">{sl.nav}</span>
                <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                  {LAYOUT_LABELS[sl.layout] || sl.layout}
                </span>
              </div>
              <button data-testid={`move-up-${sl.id}`} onClick={() => move(i, -1)} disabled={i === 0} title="Sposta su" className="p-1.5 rounded-md text-slate-400 hover:text-ink hover:bg-mist disabled:opacity-25 transition-colors">
                <ChevronUp size={16} />
              </button>
              <button data-testid={`move-down-${sl.id}`} onClick={() => move(i, 1)} disabled={i === slides.length - 1} title="Sposta giù" className="p-1.5 rounded-md text-slate-400 hover:text-ink hover:bg-mist disabled:opacity-25 transition-colors">
                <ChevronDown size={16} />
              </button>
              <button
                data-testid={`edit-slide-${sl.id}`}
                onClick={() => setOpenId(openId === sl.id ? null : sl.id)}
                className="ml-1 text-xs font-display font-semibold text-brand hover:text-brand-hover transition-colors"
              >
                {openId === sl.id ? "Chiudi" : "Modifica"}
              </button>
            </div>

            {openId === sl.id && (
              <div className="border-t border-line px-4 py-5 space-y-4 bg-mist/50">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Etichetta navigazione" value={sl.nav} onChange={(v) => patch(i, { nav: v })} testId={`slide-nav-input-${sl.id}`} />
                  <Field label="Eyebrow" value={sl.eyebrow} onChange={(v) => patch(i, { eyebrow: v })} testId={`slide-eyebrow-input-${sl.id}`} />
                </div>
                <Field label="Titolo" value={sl.title} onChange={(v) => patch(i, { title: v })} testId={`slide-title-input-${sl.id}`} />
                <Field label="Sottotitolo" value={sl.subtitle} onChange={(v) => patch(i, { subtitle: v })} textarea testId={`slide-subtitle-input-${sl.id}`} />
                {"cta" in sl && (
                  <Field label="Testo CTA" value={sl.cta} onChange={(v) => patch(i, { cta: v })} testId={`slide-cta-input-${sl.id}`} />
                )}
                {"image" in sl && (
                  <ImageField label="Immagine slide" value={sl.image} onChange={(v) => patch(i, { image: v })} testId={`slide-image-input-${sl.id}`} />
                )}

                {sl.layout === "contact" && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Ragione sociale" value={sl.company} onChange={(v) => patch(i, { company: v })} testId="contact-company-input" />
                    <Field label="Telefono" value={sl.phone} onChange={(v) => patch(i, { phone: v })} testId="contact-phone-input" />
                    <Field label="Email" value={sl.email} onChange={(v) => patch(i, { email: v })} testId="contact-email-input" />
                    <Field label="Sito web" value={sl.website} onChange={(v) => patch(i, { website: v })} testId="contact-website-input" />
                    <Field label="Indirizzo" value={sl.address} onChange={(v) => patch(i, { address: v })} testId="contact-address-input" />
                  </div>
                )}

                {Array.isArray(sl.items) && sl.layout !== "metrics" && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Elementi ({sl.items.length})</span>
                      <button data-testid={`add-item-${sl.id}`} onClick={() => addItem(i)} className="inline-flex items-center gap-1 text-xs font-display font-semibold text-brand hover:text-brand-hover">
                        <Plus size={13} /> Aggiungi
                      </button>
                    </div>
                    <div className="space-y-3">
                      {sl.items.map((it, j) => (
                        <div key={j} className="border border-line rounded-lg p-3 bg-white space-y-2">
                          <div className="flex items-start gap-2">
                            <input value={it.title || ""} onChange={(e) => patchItem(i, j, { title: e.target.value })} placeholder="Titolo" data-testid={`item-title-${sl.id}-${j}`} className={inputCls} />
                            <button data-testid={`remove-item-${sl.id}-${j}`} onClick={() => removeItem(i, j)} className="p-2 rounded-md text-slate-400 hover:text-brand hover:bg-brand-subtle transition-colors shrink-0">
                              <Trash2 size={15} />
                            </button>
                          </div>
                          <textarea rows={2} value={it.desc || ""} onChange={(e) => patchItem(i, j, { desc: e.target.value })} placeholder="Descrizione" data-testid={`item-desc-${sl.id}-${j}`} className={inputCls} />
                          {(sl.layout === "sectors" || sl.layout === "gallery") && (
                            <ImageField label="Immagine settore" value={it.image} onChange={(v) => patchItem(i, j, { image: v })} testId={`item-image-${sl.id}-${j}`} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {sl.layout === "metrics" && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Metriche ({(sl.metrics || []).length})</span>
                      <button data-testid="add-metric-button" onClick={() => addMetric(i)} className="inline-flex items-center gap-1 text-xs font-display font-semibold text-brand hover:text-brand-hover">
                        <Plus size={13} /> Aggiungi
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(sl.metrics || []).map((m, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <input value={m.value || ""} onChange={(e) => patchMetric(i, j, { value: e.target.value })} placeholder="Valore (es. 200+)" data-testid={`metric-value-${j}`} className={`${inputCls} w-32`} />
                          <input value={m.label || ""} onChange={(e) => patchMetric(i, j, { label: e.target.value })} placeholder="Etichetta" data-testid={`metric-label-${j}`} className={inputCls} />
                          <button data-testid={`remove-metric-${j}`} onClick={() => removeMetric(i, j)} className="p-2 rounded-md text-slate-400 hover:text-brand hover:bg-brand-subtle transition-colors shrink-0">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
